import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { pageScrollStyle } from "../../../styles/styles";

// Default theme configuration
const defaultThemeConfig = {
  primaryColor: "#472ed1",
  secondaryColor: "#db3a3e",
  mode: "light", // 'light' or 'dark'
  borderRadius: 8,
  fontSize: 14,
  fontFamily: "Poppins",
  compactMode: false,
  accentColor: "#00B3B0",
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const rgbToHex = (r, g, b) =>
  "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);

const lightenColor = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.floor(rgb.r + ((255 - rgb.r) * percent) / 100));
  const g = Math.min(255, Math.floor(rgb.g + ((255 - rgb.g) * percent) / 100));
  const b = Math.min(255, Math.floor(rgb.b + ((255 - rgb.b) * percent) / 100));

  return rgbToHex(r, g, b);
};

const darkenColor = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.max(0, Math.floor(rgb.r * (1 - percent / 100)));
  const g = Math.max(0, Math.floor(rgb.g * (1 - percent / 100)));
  const b = Math.max(0, Math.floor(rgb.b * (1 - percent / 100)));

  return rgbToHex(r, g, b);
};

const getContrastColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#FFFFFF";

  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;

  return brightness > 150 ? "#1C1B1F" : "#FFFFFF";
};

const deriveIconColors = (config) => ({
  primary: getContrastColor(config.primaryColor),
  secondary: getContrastColor(config.secondaryColor),
  accent: getContrastColor(config.accentColor),
});

const defaultIconColors = deriveIconColors(defaultThemeConfig);
// Theme context
const ThemeContext = createContext();

// Custom hook to use theme
export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      themeConfig: defaultThemeConfig,
      updateTheme: () => {},
      resetTheme: () => {},
      muiTheme: createTheme(createThemeOptions(defaultThemeConfig)),
      iconColors: defaultIconColors,
    };
  }
  return context;
};

// Helper function to generate theme variants
const generateColorVariants = (color) => ({
  main: color,
  light: lightenColor(color, 20),
  dark: darkenColor(color, 20),
  contrastText: getContrastColor(color),
});

const createThemeOptions = (config) => {
  const isDark = config.mode === "dark";
  const iconColors = deriveIconColors(config);

  return {
    palette: {
      mode: config.mode,
      primary: generateColorVariants(config.primaryColor),
      secondary: generateColorVariants(config.secondaryColor),
      accent: generateColorVariants(config.accentColor),
      custom: {
        iconOnPrimary: iconColors.primary,
        iconOnSecondary: iconColors.secondary,
        iconOnAccent: iconColors.accent,
      },
      text: {
        primary: isDark ? "#FFFFFF" : "#1C1B1F",
        secondary: isDark ? "#B3B3B3" : "#888",
        disabled: isDark ? "#666666" : "#CCCCCC",
      },
      background: {
        default: isDark ? "#121212" : "#F9F9F9",
        paper: isDark ? "#1E1E1E" : "#FFFFFF",
      },
      divider: isDark ? "#333333" : "#E0E0E0",
      success: {
        main: "#198754",
        light: "#28A745",
        dark: "#14532D",
        contrastText: "#FFFFFF",
      },
      error: {
        main: "#dc3545",
        light: "#FF5C6E",
        dark: "#A40020",
        contrastText: "#FFFFFF",
      },
      warning: {
        main: "#ffc107",
        light: "#FFD54F",
        dark: "#B37F00",
        contrastText: "#1C1B1F",
      },
      info: {
        main: "#0dcaf0",
        light: "#5BDFF8",
        dark: "#007B9C",
        contrastText: "#FFFFFF",
      },
    },
    typography: {
      fontFamily: [config.fontFamily, "sans-serif"].join(","),
      fontSize: config.fontSize,
      h1: { fontSize: `${config.fontSize * 2.5}px` },
      h2: { fontSize: `${config.fontSize * 2}px` },
      h3: { fontSize: `${config.fontSize * 1.75}px` },
      h4: { fontSize: `${config.fontSize * 1.5}px` },
      h5: { fontSize: `${config.fontSize * 1.25}px` },
      h6: { fontSize: `${config.fontSize * 1.1}px` },
      body1: { fontSize: `${config.fontSize}px` },
      body2: { fontSize: `${config.fontSize * 0.9}px` },
    },
    shape: {
      borderRadius: config.borderRadius,
    },
    components: {
      MuiButton: {
        defaultProps: {
          size: config.compactMode ? "small" : "medium",
        },
        styleOverrides: {
          root: {
            borderRadius: `${config.borderRadius * 0.5}rem`,
            textTransform: "capitalize",
            whiteSpace: "nowrap",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: `${config.borderRadius * 0.0625}rem`,
            boxShadow: isDark
              ? "0 4px 12px rgba(255, 255, 255, 0.1)"
              : "0 4px 12px rgba(0, 0, 0, 0.1)",
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: config.compactMode
            ? {
                ...pageScrollStyle,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  minHeight: "32px !important",
                  maxHeight: "32px !important",
                  padding: "2px 6px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  minHeight: "32px !important",
                  backgroundColor: `${config.primaryColor}1A`,
                },
              }
            : {},
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: `${config.borderRadius * 0.0625}rem`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: `${config.borderRadius * 0.0625}rem`,
            },
          },
        },
      },
    },
  };
};

const commonTheme = createTheme(createThemeOptions(defaultThemeConfig));

export const CommonThemeProvider = ({ children }) => (
  <MuiThemeProvider theme={commonTheme}>{children}</MuiThemeProvider>
);

const createCustomTheme = (config) =>
  createTheme(commonTheme, createThemeOptions(config));

// Custom Theme Provider Component
export const CustomThemeProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(() => {
    if (typeof window === "undefined") {
      return defaultThemeConfig;
    }

    const savedTheme = window.localStorage.getItem("customThemeConfig");

    try {
      return savedTheme ? JSON.parse(savedTheme) : defaultThemeConfig;
    } catch (error) {
      return defaultThemeConfig;
    }
  });

  const { primaryColor, secondaryColor, accentColor } = themeConfig;

  const iconColors = useMemo(
    () =>
      deriveIconColors({
        primaryColor,
        secondaryColor,
        accentColor,
      }),
    [primaryColor, secondaryColor, accentColor]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primary", primaryColor);
    root.style.setProperty("--primary-hover", primaryColor);
    root.style.setProperty("--primary-contrast", iconColors.primary);
  }, [iconColors.primary, primaryColor]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        "customThemeConfig",
        JSON.stringify(themeConfig)
      );
    }
  }, [themeConfig]);

  const updateTheme = useCallback((updates) => {
    setThemeConfig((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const resetTheme = useCallback(() => {
    setThemeConfig(defaultThemeConfig);
  }, []);

  const muiTheme = useMemo(() => createCustomTheme(themeConfig), [themeConfig]);

  const value = useMemo(
    () => ({
      themeConfig,
      updateTheme,
      resetTheme,
      muiTheme,
      iconColors,
    }),
    [themeConfig, updateTheme, resetTheme, muiTheme, iconColors]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
